import React, { useEffect, useState } from 'react';
import {
    Box, Grid, Typography, Table, TableBody, TableCell, TableHead, TableRow, Paper,
    TextField, Button, Select, MenuItem, FormControl, InputLabel, CircularProgress, Autocomplete
} from '@mui/material';
import { useNavigate } from 'react-router';
import Sidebar from './Sidebar';
import axios from '../axiosConfig';

const Commissions = () => {
    const navigate = useNavigate();

    // Состояния для данных о комиссиях
    const [commissions, setCommissions] = useState([]);
    const [loading, setLoading] = useState(false);

    // Параметры пагинации
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCommissions, setTotalCommissions] = useState(0);

    // Состояния для отображения форм
    const [showAddCommissionForm, setShowAddCommissionForm] = useState(false);

    // Состояния для добавления комиссии
    const [commissionType, setCommissionType] = useState('');
    const [baseAsset, setBaseAsset] = useState('');
    const [quoteAsset, setQuoteAsset] = useState('');
    const [unitType, setUnitType] = useState('');
    const [amount, setAmount] = useState('');
    const [transactionType, setTransactionType] = useState('');
    const [side, setSide] = useState('');
    const [minLimit, setMinLimit] = useState('');
    const [maxLimit, setMaxLimit] = useState('');

    // Перечисления
    const commissionTypes = ['CONVERT', 'PAY_IN', 'PAYOUT'];
    const unitTypes = ['FIXED_AMOUNT', 'PERCENT'];
    const transactionTypes = ['KSB', 'UMAI', 'SBP'];
    const sides = ['buy', 'sell'];

    // Состояния для активов
    const [assets, setAssets] = useState([]);

    useEffect(() => {
        const token = localStorage.getItem('authToken');

        if (!token || token === 'null') {
            navigate('/login');
        } else {
            fetchCommissionsData(page, rowsPerPage);
            fetchAssets(); // Загружаем активы при рендере компонента
        }
    }, [navigate, page, rowsPerPage]);

    // Функция для получения списка активов (фиатные и криптовалютные активы)
    const fetchAssets = () => {
        const cryptoAssetsEndpoint = '/transaction-info/crypto/assets';
        const fiatAssetsEndpoint = '/transaction-info/fiat/assets';

        Promise.all([
            axios.get(cryptoAssetsEndpoint),
            axios.get(fiatAssetsEndpoint)
        ])
            .then(([cryptoAssetsResponse, fiatAssetsResponse]) => {
                const cryptoAssets = cryptoAssetsResponse.data || [];
                const fiatAssets = fiatAssetsResponse.data || [];
                setAssets([...fiatAssets, ...cryptoAssets]); // Фиатные активы идут первыми
            })
            .catch((error) => {
                console.error('Ошибка при получении списка активов:', error);
            });
    };

    // Функция для получения списка комиссий
    const fetchCommissionsData = (pageNumber, pageSize) => {
        setLoading(true);
        axios.get('/commission/list')
            .then((response) => {
                const data = response.data || [];
                console.log('Полученные данные комиссий:', data);
                setCommissions(Array.isArray(data) ? data : []);
                setTotalCommissions(data.length);
                setLoading(false);
            })
            .catch((error) => {
                console.error('Ошибка при получении данных комиссий:', error);
                setLoading(false);
            });
    };

    // Функция для добавления новой комиссии
    const addCommission = () => {
        // Формируем объект комиссии с учетом необходимых полей
        const commissionData = {
            commission_type: commissionType,
            base_asset: baseAsset,
            unit_type: unitType,
            amount: Number(amount),
            min_limit: minLimit !== '' ? Number(minLimit) : -1,
            max_limit: maxLimit !== '' ? Number(maxLimit) : -1,
        };

        // Добавляем или удаляем поля в зависимости от commission_type
        if (commissionType === 'CONVERT') {
            commissionData.quote_asset = quoteAsset;
            commissionData.side = side;
        } else {
            commissionData.transaction_type = transactionType;
        }

        // Валидация перед отправкой
        const validationError = validateCommissionData(commissionData);
        if (validationError) {
            alert(validationError);
            return;
        }

        axios.post('/commission/set-commission', commissionData)
            .then(() => {
                alert('Комиссия успешно добавлена');
                // Сброс полей формы
                resetForm();
                setShowAddCommissionForm(false);
                fetchCommissionsData(page, rowsPerPage);
            })
            .catch((error) => {
                console.error('Ошибка при добавлении комиссии:', error);
                if (error.response && error.response.data.detail) {
                    alert(`Ошибка: ${error.response.data.detail}`);
                } else {
                    alert('Ошибка при добавлении комиссии');
                }
            });
    };

    // Функция для сброса полей формы добавления комиссии
    const resetForm = () => {
        setCommissionType('');
        setBaseAsset('');
        setQuoteAsset('');
        setUnitType('');
        setAmount('');
        setTransactionType('');
        setSide('');
        setMinLimit('');
        setMaxLimit('');
    };

    // Функция для удаления комиссии
    const deleteCommission = (commissionId) => {
        if (!window.confirm('Вы действительно хотите удалить эту комиссию?')) return;

        axios.delete('/commission/delete-commission', { params: { commission_id: commissionId } })
            .then(() => {
                alert('Комиссия удалена');
                fetchCommissionsData(page, rowsPerPage);
            })
            .catch((error) => {
                console.error('Ошибка при удалении комиссии:', error);
                alert('Ошибка при удалении комиссии');
            });
    };

    // Функции для пагинации
    const handlePageChange = (event, newPage) => {
        setPage(newPage);
        fetchCommissionsData(newPage, rowsPerPage);
    };

    const handleRowsPerPageChange = (event) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        setRowsPerPage(newRowsPerPage);
        setPage(0);
        fetchCommissionsData(0, newRowsPerPage);
    };

    // Функция для валидации данных комиссии перед отправкой
    const validateCommissionData = (data) => {
        const requiredFields = [];
        const unnecessaryFields = [];

        if (data.commission_type === 'CONVERT') {
            if (!data.quote_asset) requiredFields.push('quote_asset');
            if (!data.side) requiredFields.push('side');
            if (data.transaction_type) unnecessaryFields.push('transaction_type');
        } else {
            if (!data.transaction_type) requiredFields.push('transaction_type');
            if (data.quote_asset) unnecessaryFields.push('quote_asset');
            if (data.side) unnecessaryFields.push('side');
        }

        if (!data.base_asset) requiredFields.push('base_asset');
        if (!data.unit_type) requiredFields.push('unit_type');
        if (!data.amount) requiredFields.push('amount');
        if (data.min_limit > data.max_limit && data.max_limit !== -1) {
            return 'Минимальный лимит не может быть больше максимального лимита.';
        }

        if (requiredFields.length > 0) {
            return `Пожалуйста, заполните обязательные поля: ${requiredFields.join(', ')}`;
        }

        if (unnecessaryFields.length > 0) {
            return `Поля не должны быть заполнены для выбранного типа комиссии: ${unnecessaryFields.join(', ')}`;
        }

        return null;
    };

    // Функция для отображения полей формы в зависимости от типа комиссии
    const renderCommissionFormFields = () => {
        return (
            <>
                <FormControl fullWidth margin="normal">
                    <InputLabel>Тип комиссии *</InputLabel>
                    <Select
                        value={commissionType}
                        onChange={(e) => setCommissionType(e.target.value)}
                    >
                        {commissionTypes.map((type) => (
                            <MenuItem key={type} value={type}>{type}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Autocomplete
                    options={assets}
                    getOptionLabel={(option) => option}
                    value={baseAsset}
                    onChange={(event, newValue) => setBaseAsset(newValue)}
                    renderInput={(params) => <TextField {...params} label="Базовый актив" margin="normal" fullWidth />}
                />

                {commissionType === 'CONVERT' && (
                    <>
                        <Autocomplete
                            options={assets}
                            getOptionLabel={(option) => option}
                            value={quoteAsset}
                            onChange={(event, newValue) => setQuoteAsset(newValue)}
                            renderInput={(params) => <TextField {...params} label="Котируемый актив" margin="normal" fullWidth />}
                        />

                        <Autocomplete
                            options={sides}
                            getOptionLabel={(option) => option}
                            value={side}
                            onChange={(event, newValue) => setSide(newValue)}
                            renderInput={(params) => <TextField {...params} label="Сторона сделки" margin="normal" fullWidth />}
                        />
                    </>
                )}

                {commissionType !== 'CONVERT' && (
                    <Autocomplete
                        options={transactionTypes}
                        getOptionLabel={(option) => option}
                        value={transactionType}
                        onChange={(event, newValue) => setTransactionType(newValue)}
                        renderInput={(params) => <TextField {...params} label="Тип транзакции" margin="normal" fullWidth />}
                    />
                )}

                <FormControl fullWidth margin="normal">
                    <InputLabel>Тип единицы *</InputLabel>
                    <Select
                        value={unitType}
                        onChange={(e) => setUnitType(e.target.value)}
                    >
                        {unitTypes.map((type) => (
                            <MenuItem key={type} value={type}>{type}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <TextField
                    label="Сумма *"
                    variant="outlined"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    type="number"
                    fullWidth
                    margin="normal"
                />

                <TextField
                    label="Минимальный лимит"
                    variant="outlined"
                    value={minLimit}
                    onChange={(e) => setMinLimit(e.target.value)}
                    type="number"
                    fullWidth
                    margin="normal"
                />

                <TextField
                    label="Максимальный лимит"
                    variant="outlined"
                    value={maxLimit}
                    onChange={(e) => setMaxLimit(e.target.value)}
                    type="number"
                    fullWidth
                    margin="normal"
                />
            </>
        );
    };

    return (
        <Grid container spacing={2}>
            <Grid item xs={4}>
                <Sidebar />
            </Grid>
            <Grid item xs={8}>
                {!showAddCommissionForm ? (
                    <>
                        <Box mt={2}>
                            <Typography variant="h4" gutterBottom>Управление Комиссиями</Typography>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => setShowAddCommissionForm(true)}
                                style={{ marginRight: '10px' }}
                            >
                                Добавить комиссию
                            </Button>
                        </Box>

                        {/* Таблица с текущими комиссиями */}
                        <Typography variant="h6" mt={4}>Список комиссий</Typography>
                        {loading ? (
                            <CircularProgress />
                        ) : (
                            <Paper style={{ overflowX: 'auto' }}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Тип комиссии</TableCell>
                                            <TableCell>Базовый актив</TableCell>
                                            <TableCell>Котируемый актив</TableCell>
                                            <TableCell>Сторона</TableCell>
                                            <TableCell>Тип единицы</TableCell>
                                            <TableCell>Сумма</TableCell>
                                            <TableCell>Действия</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {commissions.length > 0 ? (
                                            commissions.map((commission, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>{commission.commission_type}</TableCell>
                                                    <TableCell>{commission.base_asset}</TableCell>
                                                    <TableCell>{commission.quote_asset || '-'}</TableCell>
                                                    <TableCell>{commission.side || '-'}</TableCell>
                                                    <TableCell>{commission.unit_type}</TableCell>
                                                    <TableCell>{commission.amount}</TableCell>
                                                    <TableCell>
                                                        <Button
                                                            variant="contained"
                                                            color="secondary"
                                                            onClick={() => deleteCommission(commission.id)}
                                                        >
                                                            Удалить
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={6}>Нет данных для отображения</TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </Paper>
                        )}
                    </>
                ) : (
                    <Box mt={2}>
                        <Button variant="contained" onClick={() => setShowAddCommissionForm(false)}>
                            Назад
                        </Button>
                        <Typography variant="h5" mt={2}>Добавить новую комиссию</Typography>
                        {renderCommissionFormFields()}

                        <Box mt={2}>
                            <Button variant="contained" color="primary" onClick={addCommission}>
                                Добавить комиссию
                            </Button>
                        </Box>
                    </Box>
                )}
            </Grid>
        </Grid>
    );
};

export default Commissions;
